import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import DashboardLayout from "@/components/lingo/DashboardLayout";
import { useAuth } from "@/lib/auth";
import api from "@/lib/api";
import {
  Trophy, Flame, Target, ChevronRight, Sparkles, Mic, BookOpen, Headphones, PencilLine,
  GraduationCap, Bot, FileText, Globe2, Check, History,
} from "lucide-react";

/**
 * Dashboard — EnglishTest.UK · Daylight HUD theme.
 * Hero = 4 big glowing exam cards (with skill tick boxes).
 * Below = recent attempts + quick links.
 */
const T = {
  bg: "#FCFAF3", bg2: "#F5F1E0",
  cardBg: "#FFFFFF", cardBgWarm: "#FBF6E6",
  border: "#E5DCC0", borderSoft: "#EEE6CE",
  primary: "#F77F00", primaryDark: "#D26900", primaryGlow: "rgba(247,127,0,0.45)",
  secondary: "#D62828", success: "#0C4B66",
  text: "#003049", textMuted: "rgba(0,48,73,0.65)", textOnPrimary: "#FFFFFF",
};

const SKILL_ICON = {
  Listening: Headphones,
  Reading: BookOpen,
  Writing: PencilLine,
  Speaking: Mic,
};

const EXAMS = [
  {
    id: "IELTS", name: "IELTS",
    blurb: "Academic & General · Bands 1–9",
    Icon: Globe2,
    color: "#0C4B66", color2: "#003049",
    skills: ["Listening", "Reading", "Writing", "Speaking"],
    to: "/app/exam/IELTS",
    tagline: "AI Guided · Full Mock",
  },
  {
    id: "PTE", name: "PTE Academic",
    blurb: "Pearson · Score 10–90",
    Icon: Mic,
    color: "#6B21A8", color2: "#4C1D95",
    skills: ["Speaking", "Writing", "Reading", "Listening"],
    to: "/app/exam/PTE",
    tagline: "AI Scored · Fast Results",
  },
  {
    id: "DUO", name: "Duolingo English",
    blurb: "Adaptive · Score 10–160",
    Icon: BookOpen,
    color: "#15803D", color2: "#14532D",
    skills: ["Reading", "Writing", "Listening", "Speaking"],
    to: "/app/exam/DUO",
    tagline: "On-Demand · Adaptive",
  },
  {
    id: "ELLT", name: "Oxford ELLT",
    blurb: "University admissions · CEFR",
    Icon: GraduationCap,
    color: "#1E40AF", color2: "#1E3A8A",
    skills: ["Reading", "Writing", "Listening", "Speaking"],
    to: "/app/exam/ELLT",
    tagline: "Oxford · 100+ universities",
  },
];

export default function Dashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState({ attempts: 0, lastBand: null, streak: 0, xp: 0 });
  const [recent, setRecent] = useState([]);

  useEffect(() => {
    api.get("/ielts/attempts/recent").then((r) => {
      const list = Array.isArray(r.data) ? r.data : [];
      const last = list.find((x) => x.band);
      setStats({
        attempts: list.length,
        lastBand: last ? last.band : null,
        streak: user?.streak ?? 0,
        xp: user?.xp ?? 0,
      });
      setRecent(list.slice(0, 5));
    }).catch(() => {
      setStats((s) => ({ ...s, streak: user?.streak ?? 0, xp: user?.xp ?? 0 }));
    });
  }, [user]);

  const firstName = (user?.name || "Candidate").split(" ")[0];

  return (
    <DashboardLayout>
      <div className="w-full max-w-7xl mx-auto" data-testid="student-dashboard">
        {/* HERO greeting */}
        <div className="flex flex-wrap items-end justify-between gap-4 mb-2">
          <div>
            <div className="font-mono text-[10px] tracking-[0.3em]" style={{ color: T.primary }}>
              // WELCOME BACK · {new Date().toLocaleDateString("en-GB", { weekday: "long" }).toUpperCase()}
            </div>
            <h1 className="font-display font-bold text-4xl md:text-5xl mt-1" style={{ color: T.text, letterSpacing: "-0.02em" }}>
              Hello, <span style={{ color: T.primary, textShadow: `0 0 22px ${T.primaryGlow}` }}>{firstName}</span>.
              Pick your exam.
            </h1>
            <p className="text-sm md:text-base mt-1.5 max-w-xl" style={{ color: T.textMuted }}>
              Choose your target test below. Inside, you can practise <strong style={{ color: T.text }}>with AI Guidelines</strong> (we walk you through every question) or run a full <strong style={{ color: T.text }}>Mock Test</strong> (real exam conditions).
            </p>
          </div>

          {/* Mini KPIs */}
          <div className="flex flex-wrap gap-2" data-testid="dashboard-kpis">
            {[
              { Icon: Trophy, l: "BAND",   v: stats.lastBand ? Number(stats.lastBand).toFixed(1) : "—", testid: "kpi-band" },
              { Icon: Flame,  l: "STREAK", v: `${stats.streak}d`, testid: "kpi-streak" },
              { Icon: Target, l: "XP",     v: stats.xp,           testid: "kpi-xp"     },
            ].map((k) => (
              <div key={k.l} data-testid={k.testid} className="relative px-4 py-2.5 rounded-sm"
                style={{ background: T.cardBg, border: `1px solid ${T.borderSoft}`, boxShadow: "0 4px 14px -8px rgba(0,48,73,0.10)" }}>
                <span className="absolute -top-1 -left-1 w-2.5 h-2.5 border-t-2 border-l-2" style={{ borderColor: T.primary }} />
                <span className="absolute -top-1 -right-1 w-2.5 h-2.5 border-t-2 border-r-2" style={{ borderColor: T.primary }} />
                <span className="absolute -bottom-1 -left-1 w-2.5 h-2.5 border-b-2 border-l-2" style={{ borderColor: T.primary }} />
                <span className="absolute -bottom-1 -right-1 w-2.5 h-2.5 border-b-2 border-r-2" style={{ borderColor: T.primary }} />
                <div className="flex items-center gap-2">
                  <k.Icon className="w-4 h-4" style={{ color: T.primary }} />
                  <div>
                    <div className="font-mono text-[9px] tracking-[0.25em]" style={{ color: T.textMuted }}>{k.l}</div>
                    <div className="font-display font-bold text-xl leading-none mt-0.5" style={{ color: T.text }}>{k.v}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Exam cards — the centerpiece */}
        <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-5" data-testid="exam-cards-grid">
          {EXAMS.map((e) => (
            <Link
              key={e.id}
              to={e.to}
              data-testid={`exam-card-${e.id}`}
              className="group relative overflow-hidden rounded-sm transition-all duration-300 hover:-translate-y-1"
              style={{
                background: `linear-gradient(135deg, ${e.color} 0%, ${e.color2} 100%)`,
                color: "#fff",
                minHeight: 250,
                boxShadow: `0 20px 50px -25px ${e.color}aa, 0 0 0 1px rgba(255,255,255,0.06) inset`,
              }}
              onMouseEnter={(e2) => { e2.currentTarget.style.boxShadow = `0 30px 60px -25px ${T.primary}cc, 0 0 0 1px rgba(255,255,255,0.10) inset, 0 0 30px ${T.primaryGlow}`; }}
              onMouseLeave={(e2) => { e2.currentTarget.style.boxShadow = `0 20px 50px -25px ${e.color}aa, 0 0 0 1px rgba(255,255,255,0.06) inset`; }}
            >
              {/* scanlines */}
              <div className="absolute inset-0 opacity-20 pointer-events-none" style={{
                background: "repeating-linear-gradient(0deg, rgba(255,255,255,0.06) 0px, rgba(255,255,255,0.06) 1px, transparent 1px, transparent 3px)"
              }} />
              {/* glow orb */}
              <div className="absolute -top-12 -right-12 w-48 h-48 rounded-full pointer-events-none"
                style={{ background: `radial-gradient(circle, ${T.primary}55 0%, transparent 70%)`, filter: "blur(20px)" }} />
              {/* corner brackets */}
              <span className="absolute top-2 left-2 w-4 h-4 border-t-2 border-l-2" style={{ borderColor: T.primary }} />
              <span className="absolute top-2 right-2 w-4 h-4 border-t-2 border-r-2" style={{ borderColor: T.primary }} />
              <span className="absolute bottom-2 left-2 w-4 h-4 border-b-2 border-l-2" style={{ borderColor: T.primary }} />
              <span className="absolute bottom-2 right-2 w-4 h-4 border-b-2 border-r-2" style={{ borderColor: T.primary }} />

              <div className="relative p-7 lg:p-8 h-full flex flex-col">
                <div className="flex items-start justify-between mb-4">
                  <div className="w-14 h-14 rounded-sm flex items-center justify-center"
                    style={{ background: "rgba(255,255,255,0.10)", border: "1.5px solid rgba(255,255,255,0.18)", color: T.primary, boxShadow: `0 0 14px ${T.primaryGlow}` }}>
                    <e.Icon className="w-7 h-7" />
                  </div>
                  <div className="font-mono text-[9px] tracking-[0.3em] opacity-70 group-hover:opacity-100 transition text-right">
                    {e.blurb.toUpperCase()}
                  </div>
                </div>
                <div className="font-display font-bold text-3xl md:text-4xl leading-none">{e.name}</div>

                {/* Skill tick boxes */}
                <div className="flex flex-wrap gap-1.5 mt-4" data-testid={`skills-${e.id}`}>
                  {e.skills.map((s) => {
                    const SI = SKILL_ICON[s] || BookOpen;
                    return (
                      <div key={s} className="inline-flex items-center gap-1.5 px-2 py-1 rounded-sm"
                        style={{ background: "rgba(255,255,255,0.10)", border: "1px solid rgba(255,255,255,0.18)" }}>
                        <div className="w-3.5 h-3.5 rounded-[2px] flex items-center justify-center" style={{ background: T.primary }}>
                          <Check className="w-2.5 h-2.5 text-white" strokeWidth={3} />
                        </div>
                        <SI className="w-3 h-3 opacity-80" />
                        <span className="font-mono text-[9px] tracking-[0.18em] uppercase">{s}</span>
                      </div>
                    );
                  })}
                </div>

                <div className="mt-auto pt-5 flex items-center justify-between">
                  <div className="flex items-center gap-1.5 font-mono text-[10px] tracking-[0.25em]" style={{ color: T.primary }}>
                    <Sparkles className="w-3 h-3" /> {e.tagline.toUpperCase()}
                  </div>
                  <div className="inline-flex items-center gap-1.5 font-display font-bold tracking-[0.2em] text-sm transition-all"
                    style={{ color: T.primary }}>
                    START <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </div>
                </div>
              </div>
            </Link>
          ))}
        </div>

        {/* Two-col: Recent attempts + Quick links */}
        <div className="mt-8 grid grid-cols-1 lg:grid-cols-3 gap-5">
          {/* Recent attempts */}
          <div className="lg:col-span-2 relative rounded-sm p-5"
            style={{ background: T.cardBg, border: `1px solid ${T.borderSoft}` }}
            data-testid="recent-attempts-panel">
            <span className="absolute -top-1 -left-1 w-2.5 h-2.5 border-t-2 border-l-2" style={{ borderColor: T.primary }} />
            <span className="absolute -top-1 -right-1 w-2.5 h-2.5 border-t-2 border-r-2" style={{ borderColor: T.primary }} />
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <History className="w-4 h-4" style={{ color: T.primary }} />
                <div className="font-display font-bold text-lg" style={{ color: T.text }}>Recent practice</div>
              </div>
              <Link to="/app/leaderboard" className="font-mono text-[10px] tracking-[0.25em] uppercase hover:underline"
                style={{ color: T.primary }}>
                LEADERBOARD →
              </Link>
            </div>
            {recent.length === 0 ? (
              <div className="py-7 text-center" data-testid="recent-empty">
                <div className="font-mono text-[10px] tracking-[0.25em] uppercase" style={{ color: T.textMuted }}>
                  // NO ATTEMPTS YET
                </div>
                <div className="font-display text-base mt-2" style={{ color: T.text }}>
                  Start any exam to see your bands here.
                </div>
              </div>
            ) : (
              <div className="space-y-1.5">
                {recent.map((r, i) => (
                  <div key={r.id || i} data-testid={`recent-row-${i}`}
                    className="flex items-center justify-between px-3 py-2.5 rounded-sm"
                    style={{ background: T.cardBgWarm, border: `1px solid ${T.borderSoft}` }}>
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-sm flex items-center justify-center"
                        style={{ background: T.primary, color: "#fff" }}>
                        {React.createElement(SKILL_ICON[r.module] || BookOpen, { className: "w-4 h-4" })}
                      </div>
                      <div>
                        <div className="font-display font-semibold text-sm" style={{ color: T.text }}>{r.module}</div>
                        <div className="font-mono text-[9px] tracking-[0.2em] uppercase" style={{ color: T.textMuted }}>
                          {r.format === "paper" ? "PAPER" : "COMPUTER"} · {new Date(r.completed_at).toLocaleDateString()}
                        </div>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="font-display font-bold text-xl" style={{ color: T.primary }}>
                        {Number(r.band || 0).toFixed(1)}
                      </div>
                      <div className="font-mono text-[9px] tracking-[0.2em] uppercase" style={{ color: T.textMuted }}>BAND</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Quick links (compact) */}
          <div className="grid grid-cols-2 lg:grid-cols-1 gap-3">
            {[
              { Icon: Mic,      l: "Speaking Room", to: "/app/speaking-room", c: T.primary },
              { Icon: Bot,      l: "AI Coach",      to: "/app/coach",          c: "#6B21A8" },
              { Icon: FileText, l: "All Mock Tests", to: "/app/ielts/mock",    c: "#15803D" },
              { Icon: Trophy,   l: "Leaderboard",   to: "/app/leaderboard",    c: "#D62828" },
            ].map((q) => (
              <Link key={q.l} to={q.to} data-testid={`quick-${q.l.replace(/\s/g,"-").toLowerCase()}`}
                className="group flex items-center gap-3 px-4 py-3 rounded-sm transition-all"
                style={{ background: T.cardBg, border: `1px solid ${T.borderSoft}` }}
                onMouseEnter={(e) => { e.currentTarget.style.borderColor = q.c; e.currentTarget.style.boxShadow = `0 0 14px ${q.c}33`; e.currentTarget.style.transform = "translateY(-1px)"; }}
                onMouseLeave={(e) => { e.currentTarget.style.borderColor = T.borderSoft; e.currentTarget.style.boxShadow = "none"; e.currentTarget.style.transform = ""; }}>
                <div className="w-9 h-9 rounded-sm flex items-center justify-center" style={{ background: `${q.c}1A`, color: q.c }}>
                  <q.Icon className="w-5 h-5" />
                </div>
                <div className="flex-1">
                  <div className="font-display font-semibold text-sm leading-none" style={{ color: T.text }}>{q.l}</div>
                  <div className="font-mono text-[9px] tracking-[0.25em] mt-1" style={{ color: T.textMuted }}>OPEN →</div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
